/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import Form from 'components/Form/Form';
import PropTypes from 'prop-types';
import './Rent.scss';

function useCollapsible(selector, duration = 600) {
  const ref = useRef(null);
  const animation = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const node = ref.current.querySelector(selector);
    const height = node.offsetHeight;

    animation.current = node.animate(
      [
        { maxHeight: '0px', offset: 0 },
        { maxHeight: `${height}px`, offset: 0.99 },
        { maxHeight: null, offset: 1 },
      ],
      {
        duration,
        iterations: 1,
        easing: 'ease-in-out',
      }
    );

    animation.current.pause();
    animation.current.currentTime = 1;

    return () => {
      animation.current.cancel();
    };
  }, [selector, duration]);

  useEffect(() => {
    const node = ref.current.querySelector(selector);
    node.style.overflow = 'hidden';

    function onFinish() {
      animation.current.pause();

      if (open) {
        node.style.overflow = 'visible';
      } else {
        animation.current.currentTime = 1;
      }
    }

    animation.current.addEventListener('finish', onFinish);

    if (open) {
      animation.current.playbackRate = 1;
      animation.current.play();
    } else {
      if (animation.current.currentTime > 0) {
        animation.current.reverse();
      }
    }

    return () => {
      animation.current.removeEventListener('finish', onFinish);
    };
  }, [selector, duration, open]);

  return [ref, setOpen];
}

/**
 * This is the Rent component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Rent({ formRef, id, className, title, form, onMount, ...props }) {
  const [ref, setOpen] = useCollapsible('.field-row-date');

  const renting = (() => {
    const field = form.fields.find(
      ({ id }) => id === 'demDataFields-Leier_i_dag'
    );
    if (!field) return false;
    return field.value;
  })();

  useEffect(onMount, []);

  useEffect(() => {
    setOpen(renting);
  }, [renting]);

  function renderTitle() {
    if (renting) {
      return <h3 dangerouslySetInnerHTML={{ __html: title.date }} />;
    }
    return <h3 dangerouslySetInnerHTML={{ __html: title.default }} />;
  }

  return (
    <div ref={ref} className={classnames('step step-rent', className)}>
      {renderTitle()}
      <Form ref={formRef} {...form} {...props} />
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Rent.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  title: PropTypes.shape({
    default: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
  }).isRequired,
  form: PropTypes.object.isRequired,
  onMount: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Rent.defaultProps = {
  title: {
    default: 'Default',
    date: 'Date',
  },
  form: {
    fields: [],
  },
  onMount: () => {},
  onSubmit: () => {},
};

export default React.forwardRef((props, ref) => (
  <Rent {...props} formRef={ref} />
));
